import api from './api';
import ErrorHandler from './errorHandler';

// AI extraction on a dense multi-post circular (gpt-5, high reasoning) can take
// ~90s. The shared axios client has no default timeout, so give these calls an
// explicit generous bound instead of relying on the browser/proxy cutting them off.
const AI_SCRAPE_TIMEOUT = 190000;

/**
 * Job API Service — public, read-only job-notice listing ("চাকরি").
 */
class JobService {
    /**
     * Get paginated jobs.
     * @param {object} params - { search, category, sort, page, limit }
     */
    async getJobs(params = {}) {
        try {
            const response = await api.get('/api/jobs', { params });
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Get listing stats (header cards + category chip counts).
     */
    async getStats() {
        try {
            const response = await api.get('/api/jobs/stats');
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Get a single job by numeric id or slug.
     */
    async getJob(idOrSlug) {
        try {
            const response = await api.get(`/api/jobs/${idOrSlug}`);
            return response;
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    // ─────────────────────────── Admin ───────────────────────────

    /** Admin: list jobs (all statuses). params: { search, status, category, page, limit } */
    async adminList(params = {}) {
        try {
            return await api.get('/api/jobs/admin', { params });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** Admin: get a single job for editing (by numeric id). */
    async adminGet(id) {
        try {
            return await api.get(`/api/jobs/admin/${id}`);
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** Admin: analyse a job-notice URL into form fields via AI → { data: { fields, usage } }. */
    async scrapeUrl(url) {
        try {
            return await api.post('/api/jobs/scrape-url', { url }, { timeout: AI_SCRAPE_TIMEOUT });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** Admin: analyse pasted notice text into form fields via AI → { data: { fields, usage } }. */
    async scrapeText(text) {
        try {
            return await api.post('/api/jobs/scrape-text', { text }, { timeout: AI_SCRAPE_TIMEOUT });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** Admin: analyse uploaded notice documents (images/PDF/DOCX) → { data: { fields, usage } }. */
    async scrapeFiles(files) {
        try {
            const fd = new FormData();
            for (const f of files) fd.append('files', f);
            return await api.post('/api/jobs/scrape-files', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: AI_SCRAPE_TIMEOUT,
            });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /**
     * Admin: fetch jobs from Teletalk (Alljobs) and map into form field-sets directly.
     * @returns {Promise<{ data: { jobs, total, fetched, limit } }>}
     */
    async importTeletalk(keyword = '', limit = null, parseCircular = true) {
        try {
            const body = { keyword, parseCircular };
            if (limit != null) body.limit = limit;
            return await api.post('/api/jobs/import-teletalk', body);
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** Admin: bulk-create jobs (from Teletalk import). Jobs default to draft. */
    async bulkCreate(jobs) {
        try {
            return await api.post('/api/jobs/bulk', { jobs });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** Admin: create a job. */
    async create(data) {
        try {
            return await api.post('/api/jobs', data);
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** Admin: update a job. */
    async update(id, data) {
        try {
            return await api.put(`/api/jobs/${id}`, data);
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** Admin: delete a job. */
    async remove(id) {
        try {
            return await api.delete(`/api/jobs/${id}`);
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** Admin: upload a biggapon poster image → { data: { url, key } }. */
    async uploadPosterImage(file) {
        try {
            const fd = new FormData();
            fd.append('file', file);
            return await api.post('/api/jobs/upload-poster', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** Admin: upload an organization logo (webp ≤10KB) → { data: { url, key } }. */
    async uploadLogo(file) {
        try {
            const fd = new FormData();
            fd.append('file', file);
            return await api.post('/api/jobs/upload-logo', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }

    /** Admin: AI-generate a biggapon poster background from job details → { data: { url } }. */
    async generatePoster(payload) {
        try {
            return await api.post('/api/jobs/generate-poster', payload, { timeout: 120000 });
        } catch (error) {
            throw ErrorHandler.handleError(error);
        }
    }
}

export default new JobService();
